import { useMemo, useCallback } from 'react';
import { 
  usePaginatedHelicopterListings, 
  usePaginatedEngineListings, 
  usePaginatedPartsListings, 
  usePaginatedJobListings, 
  usePaginatedServiceListings,
  PaginatedFilters 
} from './usePaginatedListings';
import { normalizeListingForCard, normalizeServiceForCard } from '@/utils/marketplaceCardNormalizer';

export type AssetType = 'helicopter' | 'engine' | 'parts' | 'jobs' | 'services';

export interface MarketplaceFilters {
  priceRange: [number, number] | null;
  seatingRange: [number, number] | null;
  tsnRange: [number, number] | null;
  yearRange: [number, number] | null;
  manufacturers: string[];
  models: string[];
  listingType: string;
  condition: string;
  location: string;
  jobCategories?: string[];
  employmentTypes?: string[];
  serviceCategories?: string[];
  aogSupportOnly?: boolean;
}

export interface BoundingBox {
  north: number;
  south: number;
  east: number;
  west: number;
}

export interface MarketplaceListingsParams {
  assetType: AssetType;
  filters: MarketplaceFilters;
  searchQuery: string;
  sellListingType: string | null;
  buyRequestType: string | null;
  partsListingType: string | null;
  marketMode: 'all' | 'sell' | 'buy';
  boundingBox?: BoundingBox | null;
  enabled?: boolean;
  sortBy?: string;
}

export interface NormalizedListing {
  id: string;
  title: string;
  price?: number;
  location?: string;
  latitude?: number | null;
  longitude?: number | null;
  images?: string[];
  featured?: boolean;
  listingType?: string;
  assetType: AssetType;
  isBuyRequest?: boolean;
  condition?: string;
  year?: number;
  sellerId?: string;
  sellerName?: string;
  visibilitySettings?: any;
  helicopterData?: any;
  engineData?: any;
  partsData?: any;
  jobData?: any;
  serviceData?: any;
  updated_at?: string;
  // Raw listing for modal access
  rawListing: any;
}

/**
 * Unified hook for marketplace listings with server-side pagination
 * Bridges filter state to asset-specific paginated hooks
 */
export function useMarketplaceListings({
  assetType,
  filters,
  searchQuery,
  sellListingType,
  buyRequestType,
  partsListingType,
  marketMode,
  boundingBox,
  enabled = true,
  sortBy,
}: MarketplaceListingsParams) {
  // Build paginated filters from marketplace state
  const paginatedFilters = useMemo((): PaginatedFilters => {
    const base: PaginatedFilters = {
      status: 'active',
      searchQuery: searchQuery || undefined,
      location: filters.location || undefined,
    };

    // Price range
    if (filters.priceRange) {
      base.priceMin = filters.priceRange[0];
      base.priceMax = filters.priceRange[1];
    }

    // Manufacturers
    if (filters.manufacturers?.length > 0) {
      base.manufacturers = filters.manufacturers;
    }

    // Sort
    base.sortBy = sortBy || 'date-desc';

    // Market mode filtering - asset-specific listing type groups
    if (marketMode === 'sell') {
      switch (assetType) {
        case 'helicopter': base.isBuyRequest = false; break;
        case 'engine': base.listingTypes = ['engine_sell', 'engine_rent']; break;
        case 'parts': base.listingTypes = ['parts_sell_aircraft', 'parts_sell_engine', 'parts_sell_general', 'parts_rent']; break;
        case 'jobs': base.listingTypes = ['job_post', 'job_contract']; break;
        case 'services': base.listingTypes = ['service_offer', 'capability_listing']; break;
      }
    } else if (marketMode === 'buy') {
      switch (assetType) {
        case 'helicopter': base.isBuyRequest = true; break;
        case 'engine': base.listingTypes = ['engine_buy', 'engine_rent_request']; break;
        case 'parts': base.listingTypes = ['parts_buy_aircraft', 'parts_buy_engine', 'parts_buy_general', 'parts_rent_request']; break;
        case 'jobs': base.listingTypes = ['job_profile', 'job_seeking']; break;
        case 'services': base.listingTypes = ['service_request', 'rfq_request']; break;
      }
    }

    // Geographic bounding box for map viewport filtering
    if (boundingBox) {
      base.boundingBox = boundingBox;
    }

    // Condition filter (applies to helicopter, engine, parts)
    if (filters.condition && filters.condition !== 'all') {
      base.condition = filters.condition;
    }

    // Engine TSN range filter
    if (assetType === 'engine' && filters.tsnRange) {
      base.tsnMin = filters.tsnRange[0];
      base.tsnMax = filters.tsnRange[1];
    }

    // Asset-specific filters
    if (assetType === 'helicopter') {
      if (filters.yearRange) {
        base.yearMin = filters.yearRange[0];
        base.yearMax = filters.yearRange[1];
      }
      if (filters.seatingRange) {
        base.seatingMin = filters.seatingRange[0];
        base.seatingMax = filters.seatingRange[1];
      }
      if (buyRequestType) {
        base.isBuyRequest = true;
        const normalizedType = buyRequestType.replace('buy_', '');
        base.listingTypes = [normalizedType];
      } else if (sellListingType) {
        base.isBuyRequest = false;
        base.listingTypes = [sellListingType];
      }
    }

    if (assetType === 'parts' && partsListingType) {
      base.listingTypes = [partsListingType];
    }

    return base;
  }, [assetType, filters, searchQuery, sellListingType, buyRequestType, partsListingType, marketMode, boundingBox, sortBy]);

  // Job-specific filters
  const jobFilters = useMemo(() => ({
    ...paginatedFilters,
    jobCategories: filters.jobCategories?.length ? filters.jobCategories : undefined,
    employmentTypes: filters.employmentTypes?.length ? filters.employmentTypes : undefined,
  }), [paginatedFilters, filters.jobCategories, filters.employmentTypes]);

  // Service-specific filters
  const serviceFilters = useMemo(() => ({
    ...paginatedFilters,
    serviceCategories: filters.serviceCategories?.length ? filters.serviceCategories : undefined,
    aogSupport: filters.aogSupportOnly || undefined,
  }), [paginatedFilters, filters.serviceCategories, filters.aogSupportOnly]);

  // Conditionally use the appropriate hook based on asset type
  const helicopterQuery = usePaginatedHelicopterListings(
    assetType === 'helicopter' && enabled ? paginatedFilters : { status: 'disabled' }
  );
  
  const engineQuery = usePaginatedEngineListings(
    assetType === 'engine' && enabled ? paginatedFilters : { status: 'disabled' }
  );
  
  const partsQuery = usePaginatedPartsListings(
    assetType === 'parts' && enabled ? paginatedFilters : { status: 'disabled' }
  );
  
  const jobsQuery = usePaginatedJobListings(
    assetType === 'jobs' && enabled ? jobFilters : { status: 'disabled' }
  );
  
  const servicesQuery = usePaginatedServiceListings(
    assetType === 'services' && enabled ? serviceFilters : { status: 'disabled' }
  );

  // Select the active query based on asset type
  const activeQuery = useMemo(() => {
    switch (assetType) {
      case 'helicopter': return helicopterQuery;
      case 'engine': return engineQuery;
      case 'parts': return partsQuery;
      case 'jobs': return jobsQuery;
      case 'services': return servicesQuery;
      default: return helicopterQuery;
    }
  }, [assetType, helicopterQuery, engineQuery, partsQuery, jobsQuery, servicesQuery]);

  // Flatten paginated data
  const allListings = useMemo(() => {
    if (!activeQuery.data?.pages) return [];
    return activeQuery.data.pages.flatMap(page => page.data);
  }, [activeQuery.data?.pages]);

  // Normalize listings for display
  const normalizedListings = useMemo((): NormalizedListing[] => {
    return allListings.map(listing => {
      const isBuyRequest = 
        listing.is_buy_request === true ||
        (listing.listing_type || '').includes('buy') ||
        (listing.listing_type || '').includes('request');

      const normalized = assetType === 'services' 
        ? normalizeServiceForCard(listing)
        : normalizeListingForCard(listing, assetType, isBuyRequest);

      return {
        ...normalized,
        assetType,
        rawListing: listing,
        updated_at: listing.updated_at || listing.created_at,
      } as NormalizedListing;
    });
  }, [allListings, assetType]);

  // Total count from first page
  const totalCount = useMemo(() => {
    const firstPage = activeQuery.data?.pages?.[0];
    return firstPage?.totalCount ?? 0;
  }, [activeQuery.data?.pages]);

  // Listings with coordinates for map
  const listingsWithCoordinates = useMemo(() => {
    return normalizedListings.filter(l => l.latitude && l.longitude);
  }, [normalizedListings]);

  // Extract unique manufacturers for filter dropdowns
  const manufacturers = useMemo(() => {
    const mfrs = new Set<string>();
    allListings.forEach(listing => {
      const manufacturer = 
        listing.helicopters?.manufacturer || 
        listing.manufacturer;
      if (manufacturer) mfrs.add(manufacturer);
    });
    return Array.from(mfrs).sort();
  }, [allListings]);

  // Stable fetch next page handler
  const fetchNextPage = useCallback(() => {
    if (activeQuery.hasNextPage && !activeQuery.isFetchingNextPage) {
      activeQuery.fetchNextPage();
    }
  }, [activeQuery]);

  return {
    // Data
    listings: normalizedListings,
    allListings, // Raw listings for modals
    listingsWithCoordinates,
    manufacturers,
    totalCount,
    
    // Pagination state
    hasNextPage: activeQuery.hasNextPage ?? false,
    isFetchingNextPage: activeQuery.isFetchingNextPage,
    fetchNextPage,
    
    // Query state
    isLoading: activeQuery.isLoading,
    isError: activeQuery.isError,
    error: activeQuery.error,
    
    // Refetch
    refetch: activeQuery.refetch,
  };
}

export default useMarketplaceListings;
